/**
 * CRM Events transport: the real Operations backend.
 *
 * Powers the Operations page's five "Automated Operations" totals, the
 * promotions/campaigns list, and the live Operations feed (the
 * Operation / Category / Scope / Channel / Status table).
 *
 *   GET {base}/overview?tenant_id&site_id            -> the five operations totals
 *   GET {base}/campaigns?tenant_id&site_id&page&…    -> paged items + the same totals
 *   GET {base}/live-events?tenant_id&…&status&…      -> the live Operations feed (paged)
 *
 * The five `/overview` totals line up 1:1 with the Operations "Automated
 * Operations" tiles (`AutomatedStat`):
 *   campaigns_total          -> Campaigns         (megaphone)
 *   players_targeted_total   -> Targeted Players  (people)
 *   sent_notifications_total -> Sent Notifications(bell)
 *   players_activated_total  -> Activated Players (member)
 *   support_cases_total      -> Support Cases     (handshake)
 *
 * Reached through the owner-hub MLP proxy (`_svc=ce`, cluster scope), so every call
 * carries the brand `tenant_id` and authenticates with the shared raw owner-hub
 * token. CRM Events serves its routes under `/api/v1`, so the transport prepends
 * that prefix and forwards the literal multi-segment path — see `utils/mlp-proxy.ts`. A
 * 401/403 from the proxy rejects to the caller (the tiles fall back to `N/A`)
 * without touching the owner-hub session.
 *
 * Full request/response contract: `docs/api.owners-hub.operations.md`.
 */
import axios from 'axios';

import { createMlpProxyTransport } from '../../../utils/mlp-proxy';

const OVERVIEW_PATH = '/overview';
const CAMPAIGNS_PATH = '/campaigns';
const LIVE_EVENTS_PATH = '/live-events';

/* ── transport ───────────────────────────────────────────────────────────────
 * CRM Events is a CLUSTER service behind the owner-hub MLP proxy (`_svc=ce`, served
 * under `/api/v1`): every call carries the brand `tenant_id`, authenticates with the
 * raw owner-hub token, and opts out of the auth interceptor's refresh-and-teardown so
 * a downstream 401/403 can't log the user out. See `utils/mlp-proxy.ts`. */
const { url, requestConfig } = createMlpProxyTransport({
  svc: 'ce',
  scope: 'c',
  pathPrefix: '/api/v1',
  headers: { Accept: 'application/json' },
});

/* ── shapes ─────────────────────────────────────────────────────────────────── */

/**
 * The five operations totals, returned by `/overview` and echoed as the `summary`
 * of `/campaigns`. They feed the Operations "Automated Operations" tiles 1:1
 * (see this module's header for the icon/label mapping).
 */
export interface OperationsOverview {
  campaigns_total: number;
  players_targeted_total: number;
  players_activated_total: number;
  sent_notifications_total: number;
  support_cases_total: number;
}

/** `GET /overview` envelope. */
export interface OperationsOverviewResponse {
  overview: OperationsOverview;
}

/**
 * The per-benefit uplift scores a campaign carries (the +N Conversion /
 * Monetization / Retention numbers shown on the promotion card and detail hero).
 */
export interface OperationsBenefitScores {
  conversion?: number;
  monetization?: number;
  retention?: number;
}

/**
 * One row of `GET /campaigns` (an operations promotion/campaign), confirmed
 * against a tenant with live CRM data (tenant 60528). Every field is optional so
 * the mapper (`api/campaign-map.ts`) reads it defensively and never throws when a
 * key is absent. Maps to the FE `Promotion` (`mock-data/types.ts`).
 *
 * Confirmed fields: `id` (e.g. `crm:6a36…`), `title`, `status` + `ui_status`
 * (`suggestion`/`running`/`paused`/`ended`), `type` (`Acquisition`/`Promo`),
 * `benefits` (label list) + `benefit_scores` (the numeric uplift), `image_url`
 * (CDN thumbnail), `start_at`/`end_at` (ISO), `scope`, `channels`, `tags`,
 * `campaign_origin`.
 */
export interface OperationsCampaignItem {
  id?: string;
  campaign_id?: string;
  tenant_id?: string;
  title?: string;
  name?: string;
  status?: string;
  /** Display status the backend already resolved; preferred over `status`. */
  ui_status?: string;
  /** `Acquisition` / `Promo` — maps to the FE filter-chip category. */
  type?: string;
  scope?: string;
  channels?: string[];
  /** Benefit labels (e.g. `["Conversion","Monetization","Retention"]`). */
  benefits?: string[];
  /** Numeric per-benefit uplift; drives the `+N` benefit chips. */
  benefit_scores?: OperationsBenefitScores;
  tags?: string[];
  /** CDN thumbnail URL (the card/hero artwork). */
  image_url?: string;
  /** Legacy/alternate artwork key, read as a fallback. */
  image?: string;
  /** ISO campaign window start / end. */
  start_at?: string;
  end_at?: string;
  campaign_origin?: string;
}

/**
 * `GET /campaigns/{id}` — one campaign's full detail. Extends the list item with
 * the detail-only fields the Running/paused promotion hero shows: `description`
 * (hero subtitle), `offering` (the concrete reward, its `name` is the hero
 * "Offering" value) and `players_engaged` (the hero's first stat).
 */
export interface OperationsCampaignDetail extends OperationsCampaignItem {
  description?: string;
  offering?: { kind?: string; name?: string };
  players_engaged?: number;
}

/** `GET /campaigns`: a page of items plus the same five totals as `summary`. */
export interface OperationsCampaignsResponse {
  campaigns: {
    items: OperationsCampaignItem[];
    page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
  };
  summary: OperationsOverview;
  /** The server's echo of the filters it applied (tenant_id/site_id/page/page_size). */
  applied_filters: Record<string, unknown>;
}

/* ── calls ──────────────────────────────────────────────────────────────────── */

export interface OperationsScope {
  /** Owner tenant id (`window.config.clientId`). */
  tenantId: number | string;
  /** Optional site scope; omitted = all the tenant's sites. */
  siteId?: number | string;
}

/** Fetch the five operations totals for a tenant (optionally one site). */
export const getOperationsOverview = async (
  scope: OperationsScope,
  signal?: AbortSignal,
): Promise<OperationsOverview> => {
  const { data } = await axios.get<OperationsOverviewResponse>(
    url(OVERVIEW_PATH, scope.tenantId),
    requestConfig(signal, {
      ...(scope.siteId != null ? { site_id: scope.siteId } : {}),
    }),
  );
  return data.overview;
};

export interface ListOperationsCampaignsParams extends OperationsScope {
  /** 1-based page index (default 1). */
  page?: number;
  /** Page size (default 20). */
  pageSize?: number;
}

/** List operations promotions/campaigns for a tenant (with the totals summary). */
export const listOperationsCampaigns = async (
  params: ListOperationsCampaignsParams,
  signal?: AbortSignal,
): Promise<OperationsCampaignsResponse> => {
  const { data } = await axios.get<OperationsCampaignsResponse>(
    url(CAMPAIGNS_PATH, params.tenantId),
    requestConfig(signal, {
      ...(params.siteId != null ? { site_id: params.siteId } : {}),
      ...(params.page != null ? { page: params.page } : {}),
      ...(params.pageSize != null ? { page_size: params.pageSize } : {}),
    }),
  );
  return data;
};

/** Fetch one campaign's full detail by id (the promotion detail page). */
export const getOperationsCampaign = async (
  id: string,
  scope: OperationsScope,
  signal?: AbortSignal,
): Promise<OperationsCampaignDetail> => {
  const { data } = await axios.get<OperationsCampaignDetail>(
    // The id (e.g. `crm:6a36…`) is forwarded as its own path segment; the transport
    // `encodeURIComponent`-escapes it, so the reserved `:` stays safe.
    url(`${CAMPAIGNS_PATH}/${id}`, scope.tenantId),
    requestConfig(signal, {
      ...(scope.siteId != null ? { site_id: scope.siteId } : {}),
    }),
  );
  return data;
};

/* ── live events (the live Operations feed) ───────────────────────────────────── */

/**
 * One row of `GET /live-events`: an automated operation that ran (or is due to
 * run) for the tenant. This is the Time / Operation / Category / Scope / Channel /
 * Status table on the Operations page.
 *
 * **Confirmed** against a tenant with live CRM data (tenant 1000019, ~3.2k events):
 * every item carries `id` (`crm_action:<hex>`), `tenant_id`, `timestamp` (ISO,
 * newest first and INCLUDING future-dated rows), `title`, `category`, `scope`,
 * `channel` + `channels`, and `status`. `error_message` appears only on
 * `not_sent` rows (observed value: `Blocked by comm flags`).
 *
 *   title (or operation/name) -> the "Operation" column
 *   timestamp                 -> "Time"      (future-dated = still scheduled)
 *   category                  -> "Category"  (Conversion, Retention, …)
 *   scope                     -> "Scope"     (Multiple audiences, Non-depositor, …)
 *   channel                   -> "Channel"   (Mail, Push, In-app, SMS)
 *   status                    -> "Status"    (`scheduled` / `sent` / `not_sent`)
 *
 * The index signature is deliberate: the mapper still reads the alternate keys
 * (`operation`, `name`, `at`, `created_at`, `event_id`) defensively, so a shape
 * drift downgrades a cell rather than throwing.
 */
export interface LiveEventItem {
  /** e.g. `crm_action:6a70dcb41cf9fad889381d85`. */
  id?: string;
  tenant_id?: string;
  /** ISO timestamp; future-dated while the operation is still scheduled. */
  timestamp?: string;
  title?: string;
  category?: string;
  scope?: string;
  channel?: string;
  channels?: string[];
  /** `scheduled` | `sent` | `not_sent` (see `useOperationsLiveEvents`). */
  status?: string;
  /** Why a `not_sent` operation never went out (e.g. `Blocked by comm flags`). */
  error_message?: string;
  [key: string]: unknown;
}

/**
 * `GET /live-events`: a page of live-feed rows. Same pagination envelope as
 * `/campaigns` but keyed `live_events`, and with NO `summary` block (those totals
 * live on `/overview`). `applied_filters` echoes the filters the server applied.
 */
export interface LiveEventsResponse {
  live_events: {
    items: LiveEventItem[];
    page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
  };
  applied_filters: Record<string, unknown>;
}

export interface ListLiveEventsParams extends OperationsScope {
  /** Filter by status (e.g. `Executed`). Server-side; unknown values match nothing. */
  status?: string;
  /** Filter by category (e.g. `Conversion`). */
  category?: string;
  /** Filter by scope (e.g. `Win-back`). */
  scope?: string;
  /** Filter by channel (e.g. `Push`). */
  channel?: string;
  /** 1-based page index (default 1). */
  page?: number;
  /** Page size (default 20). */
  pageSize?: number;
}

/** List the live Operations feed for a tenant (newest first), with optional filters. */
export const listLiveEvents = async (
  params: ListLiveEventsParams,
  signal?: AbortSignal,
): Promise<LiveEventsResponse> => {
  const { data } = await axios.get<LiveEventsResponse>(
    url(LIVE_EVENTS_PATH, params.tenantId),
    requestConfig(signal, {
      ...(params.siteId != null ? { site_id: params.siteId } : {}),
      ...(params.status != null ? { status: params.status } : {}),
      ...(params.category != null ? { category: params.category } : {}),
      ...(params.scope != null ? { scope: params.scope } : {}),
      ...(params.channel != null ? { channel: params.channel } : {}),
      ...(params.page != null ? { page: params.page } : {}),
      ...(params.pageSize != null ? { page_size: params.pageSize } : {}),
    }),
  );
  return data;
};
